import type { TFunction } from 'i18next';

export type TwoFactorChannelMessageNs = 'settings' | 'auth';
export type ChannelNotificationMethod = 'email' | 'telegram' | 'bale';

const DEV_CODE_RE = /Development code:\s*(\d{6})/i;
const SENT_TO_MESSAGING_RE = /verification code was sent to your (Telegram|Bale)/i;
const SENT_TO_EMAIL_RE = /verification code was sent to (.+)\./i;

export interface ResolvedTwoFactorChannelMessage {
  message: string;
  duration?: number;
  isDevelopment: boolean;
}

function prefix(ns: TwoFactorChannelMessageNs): string {
  return ns === 'settings' ? 'security' : 'twoFactor';
}

function resolveSentMessage(
  t: TFunction,
  ns: TwoFactorChannelMessageNs,
  method?: ChannelNotificationMethod
): string {
  const base = prefix(ns);
  if (method === 'telegram') {
    return t(`${base}.channelCodeSentTelegram`);
  }
  if (method === 'bale') {
    return t(`${base}.channelCodeSentBale`);
  }
  if (method === 'email') {
    return t(`${base}.channelCodeSentEmail`);
  }
  return t(`${base}.codeSent`);
}

/** Map English API responses to localized UI copy (success paths only). */
export function resolveTwoFactorChannelMessage(
  apiMessage: string | undefined | null,
  t: TFunction,
  ns: TwoFactorChannelMessageNs,
  method?: ChannelNotificationMethod
): ResolvedTwoFactorChannelMessage {
  const base = prefix(ns);
  const fallback = resolveSentMessage(t, ns, method);
  const trimmed = apiMessage?.trim();
  if (!trimmed) {
    return { message: fallback, isDevelopment: false };
  }

  const devMatch = trimmed.match(DEV_CODE_RE);
  if (devMatch) {
    return {
      message: t(`${base}.developmentCode`, { code: devMatch[1] }),
      duration: 60000,
      isDevelopment: true
    };
  }

  const messagingMatch = trimmed.match(SENT_TO_MESSAGING_RE);
  if (messagingMatch) {
    const channel = messagingMatch[1].toLowerCase() as 'telegram' | 'bale';
    return {
      message: resolveSentMessage(t, ns, channel),
      isDevelopment: false
    };
  }

  if (SENT_TO_EMAIL_RE.test(trimmed)) {
    return {
      message: resolveSentMessage(t, ns, 'email'),
      isDevelopment: false
    };
  }

  return { message: fallback, isDevelopment: false };
}
